import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { baseURL } from "config";
import { logOut, setAuthToken, setOriginalRoleAction, setRoleAction, setUser } from "redux/features/authSlice/AuthSlice";
import { store } from "redux/store";
import { globalVariables } from "./globalVariables";
import { setModalClose, setToastConfig } from "redux/globalSlice";
import { setMemberDetails } from "redux/features/memberSlice/memberSlice";
import { setHostDetails, setTrialDetails } from "redux/features/hostSlice/addTrialSlice/addTrialSlice";
import * as Sentry from "@sentry/react";

const httpLink = createHttpLink({
  uri: `${baseURL}/graphql`,
});

const authLink = setContext((_, { headers }) => {
  const token = store.getState().auth?.authReducer?.token;

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const clearStore = () => {
  store.dispatch(setModalClose());
  store.dispatch(logOut());
  store.dispatch(setUser(null));
  store.dispatch(setAuthToken(""));
  store.dispatch(setMemberDetails(null));
  store.dispatch(setHostDetails(null));
  store.dispatch(setTrialDetails(null));
  store.dispatch(setRoleAction(""));
  store.dispatch(setOriginalRoleAction(""));
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      console.log("ERROR IN GQL ", err.message);

      // Send GraphQL errors to Sentry with context
      Sentry.captureException(new Error(err.message), {
        contexts: {
          graphql: {
            operationName: operation.operationName,
            variables: operation.variables,
            query: operation.query.loc?.source?.body,
          },
        },
        tags: {
          errorType: "GraphQL",
          errorCode: err?.extensions?.code,
        },
        level: err?.extensions?.code === "UNAUTHENTICATED" ? "info" : "error",
      });

      switch (err?.extensions?.code) {
        case "UNAUTHENTICATED":
          store.dispatch(
            setToastConfig({
              show: true,
              message: globalVariables.sessionExpire,
              callBack: () => {
                clearStore();
              },
            })
          );
          break;
        default: {
          if(err.message === "Please authenticate") {
            clearStore();
            window.location.href = "/signin";
          }
          if (
            err.message === "Please authenticate" ||
            err.message.includes("401") ||
            err.statusCode == 401
          ) {
            store.dispatch(
              setToastConfig({
                show: true,
                message: globalVariables.sessionExpire,
                callBack: () => {
                  clearStore();
                },
              })
            );
          } else {
            console.log("Else block error ", err);
          }
        }
      }
    }
  }

  // Send network errors to Sentry
  if (networkError) {
    console.error("Network error:", networkError);
    Sentry.captureException(networkError, {
      tags: {
        errorType: "Network",
      },
    });
  }

  return forward(operation);
});

const authHttpLink = authLink.concat(httpLink);

export const client = new ApolloClient({
  link: from([errorLink, authHttpLink]),
  cache: new InMemoryCache(),
});
